import { RedTeamTaskIcon } from '@/components/RedTeamTaskIcon'
import { useRef, useState } from 'react'
import { ModelLabel, modelDisplayName } from '@/components/ModelLabel'
import { fmtDateTime } from '@/lib/format'
import type { BoardJob } from '@/lib/task-runs'
import { Score } from './ExecutionStatus'

function rolloutSummary(job: BoardJob) {
  const name = job.model ? modelDisplayName(job.model) : `${job.kind} check`
  const steps = job.kind === 'model' && job.step != null ? ` after ${job.step} ${job.step === 1 ? 'step' : 'steps'}` : ''
  if (job.status === 'queued') return `${name} is queued for execution.`
  if (job.status === 'running') return `${name} is running${job.kind === 'model' && job.step != null ? ` at step ${job.step}` : ''}.`
  if (job.status === 'failed') return `${name} failed${steps}; open the rollout for failure details.`
  if (job.status === 'terminated') return `${name} was stopped${steps}.`
  return `${name} completed${steps}${job.score == null ? ' with no recorded verifier score' : ` with a verifier score of ${job.score.toFixed(2)}`}.`
}

function Preview({ job, refresh }: { job: BoardJob; refresh: () => void }) {
  const [failed, setFailed] = useState<string | null>(null)
  const image = useRef<{ key: string; url: string; at: number }>()
  const key = `${job.attempt_id}/${job.preview?.key ?? job.preview?.sequence}`
  if (job.preview && (image.current?.key !== key || failed === image.current?.url || Date.now() - image.current.at > 600000)) {
    image.current = { key, url: job.preview.url, at: Date.now() }
  }
  if (!job.preview || failed === image.current?.url) return <span className="rollout-placeholder">
    {job.status === 'running' ? 'Waiting for screenshot' : 'No screenshot recorded'}
  </span>
  return <img src={image.current?.url} alt={`${job.model ?? job.kind} rollout screenshot`} loading="lazy"
    onError={() => { setFailed(image.current!.url); refresh() }} />
}

export function TaskRolloutCards({ jobs, onSelect, refresh, showTask = true }: {
  jobs: BoardJob[]; onSelect: (job: BoardJob) => void; refresh: () => void; showTask?: boolean
}) {
  const lastRefresh = useRef(0)
  const refreshOnce = () => {
    if (Date.now() - lastRefresh.current < 30000) return
    lastRefresh.current = Date.now()
    refresh()
  }
  return <div className="task-rollout-grid">{jobs.map(job => <button type="button" className="task-rollout-card"
    key={`${job.experiment_id}/${job.job_id}`} title={rolloutSummary(job)} onClick={() => onSelect(job)}>
    <div className="rollout-preview"><Preview job={job} refresh={refreshOnce} />
      <span className={`rollout-badge ${job.status}`}>{job.status === 'running' ? 'Live' : job.status}</span>
      {job.preview?.kind === 'last_step' && job.status !== 'running' && <span className="preview-caption" title="Last recorded step; final screenshot unavailable">Last step</span>}
    </div>
    <div className="rollout-card-body">
      {showTask && <strong><RedTeamTaskIcon task={job} />{job.task_title ?? job.task_id}</strong>}
      {job.model ? <ModelLabel model={job.model} /> : <strong>{job.kind} check</strong>}
      <div className="rollout-card-metrics"><span title="Verifier score"><Score value={job.score} /></span><span className="rollout-step-count">{job.step ?? 0} {job.step === 1 ? 'step' : 'steps'}</span></div>
      <small className="rollout-card-experiment" title={`Experiment ${job.experiment_id}`}>{job.experiment_id}</small>
      <small className="rollout-card-date">{fmtDateTime(job.finished_at ?? job.created_at)}{showTask && job.package_commit && <code title="Task package">{job.package_commit.slice(0,7)}</code>}</small>
      {job.history_complete === false && <small>Incomplete step history</small>}
    </div>
  </button>)}</div>
}

export function PageButtons({ offset, next, onChange, pageSize = 50 }: { offset: number; next: number | null; onChange: (value: number) => void; pageSize?: number }) {
  if (!offset && next == null) return null
  return <div className="task-pagination">
    <button className="btn" disabled={!offset} onClick={() => onChange(Math.max(0, offset - pageSize))}>Previous</button>
    <span>Page {Math.floor(offset / pageSize) + 1}</span>
    <button className="btn" disabled={next == null} onClick={() => next != null && onChange(next)}>Next</button>
  </div>
}
